import { useEffect, useState } from "react";
import { api, formatRupiah, dispDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { PageLoader } from "@/components/PageLoader";
import { Wallet, TrendingUp, CheckCircle2 } from "lucide-react";

export default function EmployeePhlWallet() {
  const [summary, setSummary] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/phl/summary/me").then((r) => setSummary(r.data)),
      api.get("/phl/payouts/me").then((r) => setPayouts(r.data)),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) return <PageLoader label="Memuat saldo upah..." />;
  if (!summary) return <div className="p-6 text-sm text-zinc-500">Data tidak tersedia.</div>;

  return (
    <div className="px-4 sm:px-6 py-6 max-w-2xl mx-auto pb-24" data-testid="employee-phl-wallet-page">
      <p className="overline">Upah PHL</p>
      <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Saldo Upah Saya</h1>

      {/* Main balance card */}
      <Card className="mt-6 p-6 border border-amber-300 bg-gradient-to-br from-amber-50 to-amber-100 shadow-none">
        <div className="flex items-center gap-2 text-amber-800 mb-1">
          <Wallet className="w-4 h-4" />
          <p className="overline text-[0.65rem]">Belum Diambil</p>
        </div>
        <p className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-amber-900" data-testid="my-phl-balance">
          {formatRupiah(summary.balance)}
        </p>
        <p className="text-xs text-amber-800 mt-2">
          Rate: Rp {summary.hourly_rate.toLocaleString("id-ID")}/jam
        </p>
      </Card>

      {/* Sub summary */}
      <div className="grid grid-cols-2 gap-3 mt-3">
        <Card className="p-4 border border-zinc-200 shadow-none">
          <div className="flex items-center gap-1.5 text-zinc-500">
            <TrendingUp className="w-3.5 h-3.5" />
            <p className="overline text-[0.55rem]">Total Terkumpul</p>
          </div>
          <p className="font-display text-lg font-bold tracking-tight mt-1" data-testid="my-phl-earned">
            {formatRupiah(summary.total_earned)}
          </p>
        </Card>
        <Card className="p-4 border border-zinc-200 shadow-none">
          <div className="flex items-center gap-1.5 text-emerald-700">
            <CheckCircle2 className="w-3.5 h-3.5" />
            <p className="overline text-[0.55rem]">Sudah Diambil</p>
          </div>
          <p className="font-display text-lg font-bold tracking-tight text-emerald-700 mt-1" data-testid="my-phl-paid">
            {formatRupiah(summary.total_paid)}
          </p>
        </Card>
      </div>

      {/* Payout history */}
      <div className="mt-6">
        <p className="overline mb-2">Riwayat Pengambilan</p>
        {payouts.length === 0 ? (
          <Card className="p-8 text-center border border-zinc-200 shadow-none">
            <p className="text-sm text-zinc-500">Belum ada riwayat pengambilan.</p>
          </Card>
        ) : (
          <div className="space-y-2">
            {payouts.map((p) => (
              <Card key={p.id} className="p-3 border border-zinc-200 shadow-none" data-testid={`my-payout-${p.id}`}>
                <div className="flex justify-between items-start gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm">{dispDate(p.paid_date)}</p>
                    {p.note && <p className="text-xs text-zinc-500 mt-0.5">{p.note}</p>}
                  </div>
                  <p className="font-display font-bold text-emerald-700 flex-shrink-0">
                    {formatRupiah(p.amount)}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
